import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import { setDataList, setSelectAll, setSelectedItem, setSelectedRows } from '../../components/PerinHealthGridSlice';
import styles from './ContentManagement.module.css';
import { useNavigate } from 'react-router-dom';
import useAxios from '../../services/AxiosConfig';
import { setReloadGrid } from './ContentManagementSlicer';
import FilterManagementView from '../../components/FilterManagementView';
import { resetAddEditContent, setFieldValue, toggleUpdateMode } from './add-edit-content/AddEditContentSlice';
import { resetFilterState } from '../../slicers/FilterSlice';
import { closeModal } from '../../components/PerinHealthModelSlice';
import managementStyles from '../ManagementPages.module.css';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { formatDateString } from '../../utils/dateUtils';
import { openModal } from '../../components/PerinHealthModelSlice';

const ContentManagement = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userId = useSelector((state) => state.userInfo.id);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const contentManagementState = useSelector((state) => state.contentManagement);
    const reloadGrid = contentManagementState.reloadGrid;
    const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
    const [activeContentIds, setActiveContentIds] = useState([]);

    useEffect(() => {
        if (reloadGrid) {
            getContentById();
        }
    }, [reloadGrid]);

    useEffect(() => {
        getCarePrograms();
    }, []);

    useEffect(()=> {
        dispatch(setSelectedItem({}));
    }, []);

    const getCarePrograms = () => {
        httpService.get(`careprogram/list/${userId}`)
        .then((responseData) => {
            if (Array.isArray(responseData.data.data)) {
                const uniqueContentIds = new Set();
                for (const program of responseData.data.data) {
                    for (const content of program.contents) {
                        uniqueContentIds.add(content.contentId);
                    }
                }
                setActiveContentIds(Array.from(uniqueContentIds));
            } else {
                setActiveContentIds([]);
            }
        })
        .catch((err) => {
            console.log(err);
            setActiveContentIds([]);
        });
    };

    useEffect(() => {
        console.log('Date format changed:', {
            newFormat: userDateFormat,
            hasData: perinHealthGrid?.dataList?.length > 0
        });
        
        if (perinHealthGrid?.dataList && perinHealthGrid.dataList.length > 0) {
            const reformattedData = perinHealthGrid.dataList.map(item => {
                const newItem = {
                    ...item,
                    date_created: formatDateString(item.file?.LastModified, userDateFormat)
                };
                return newItem;
            });
            dispatch(setDataList(reformattedData));
        }
    }, [userDateFormat]);

    const getContentById = () => {
        dispatch(resetFilterState());
        httpService.get('content/list/' + userId)
            .then((responseData) => {
                dispatch(setDataList([]));
                dispatch(setReloadGrid(false));
                if (Array.isArray(responseData.data.data)) {
                    const contentFormatted = responseData.data.data.map((item) => {
                        const type = item.file?.ContentType ? item.file?.ContentType : item.linkUrl ? 'link' : 'Unknown';
                        const formattedItem = {
                            ...item,
                            type: type,
                            date_created: formatDateString(item.date_created, userDateFormat)
                        };
                        return formattedItem;
                    });
                    dispatch(setDataList(contentFormatted));
                } else {
                    dispatch(setDataList([]));
                }
            })
            .catch((err) => {
                console.error('Error fetching content:', err);
                dispatch(setDataList([]));
            });
    };

    const onRowClickHandler = (rowItem) => {
        dispatch(resetAddEditContent());
        dispatch(setSelectedItem(rowItem));
        dispatch(setFieldValue(rowItem));
        dispatch(toggleUpdateMode(true));
        navigate('/add-edit-content');
    };

    const showDeleteModal = () => {
        const contentToDelete = [];
        const contentCannotDelete = [];
        
        perinHealthGrid.selectedRows.forEach(content => {
            if (activeContentIds.includes(content.id)) {
                contentCannotDelete.push(content);
            } else {
                contentToDelete.push(content);
            }
        });

        const cannotDeleteNames = contentCannotDelete.map(content => content.name || content.id).join('\n');
        const canDeleteNames = contentToDelete.map(content => content.name || content.id).join('\n');

        // If all content cannot be deleted, show error alert and return early
        if (contentCannotDelete.length > 0 && contentToDelete.length === 0) {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: 'Cannot Delete Content',
                    message: `The following content cannot be deleted because they are assigned to a care program:\n\n<i>${cannotDeleteNames}</i>`,
                    buttons: [
                        { label: 'Ok', variant: 'primary', onClickKey: 'OK' },
                    ],
                })
            );
            return;
        } else if (contentCannotDelete.length > 0 && contentToDelete.length > 0) {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: 'Partial Delete Warning',
                    message: `The following content <strong>will</strong> be deleted:\n<i>${canDeleteNames}</i>\n\nThe following content <strong>cannot</strong> be deleted because they are currently assigned to care programs:\n<i>${cannotDeleteNames}</i>`,
                    buttons: [
                       
                        { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                        { label: 'Confirm', variant: 'primary', onClickKey: 'YES' },
                    ],
                })
            );
        } else {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: 'Delete Content?',
                    message: `The following content will be deleted:\n<i>${canDeleteNames}</i>`,
                    buttons: [
                       
                        { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                        { label: 'Confirm', variant: 'primary', onClickKey: 'YES' },
                    ],
                })
            );
        }
    };

    const onDeleteHandler = () => {
        dispatch(closeModal());

        const contentToDelete = [];
        
        perinHealthGrid.selectedRows.forEach(content => {
            if (!activeContentIds.includes(content.id)) {
                contentToDelete.push(content);
            }
        });

        const deletePromises = contentToDelete.map((selectedItem) => {
            const itemId = selectedItem.id;
            return httpService.delete(`content/${itemId}`);
        });

        Promise.all(deletePromises)
            .then((responses) => {
                dispatch(setSelectedRows([]));
                dispatch(setSelectAll(false));
                dispatch(setReloadGrid(true));
                dispatch(closeModal());
                
                // Show success message only if some content was actually deleted
                if (contentToDelete.length > 0) {
                    dispatch(
                        showAlert({
                            header: "Success",
                            message: `Successfully deleted ${contentToDelete.length} content item(s)`,
                            type: 'success'
                        })
                    );
                }
            })
            .catch((errors) => {
                let errorMessage = "There was an error deleting content";
                if (errors?.response?.data?.message) {
                    errorMessage = errors?.response?.data?.message;
                }
                console.error('Error deleting rows:', errors);
                dispatch(
                    showAlert({
                        header: "Error",
                        message: errorMessage,
                        type: 'danger'
                    })
                );
            });
    }

    const onAddContentHandler = () => {
        dispatch(resetAddEditContent());
        navigate('/add-edit-content');
    }

    const headers = [
        { key: 'id', label: "ID" },
        { key: 'name', label: "Name" },
        { key: 'type', label: "Type" },
        { key: 'groupName', label: "Group" },
        { key: 'date_created', label: 'Date Created' }

    ];

    return (
        <div className={managementStyles.MiddleContent}>
            <FilterManagementView
                isOrganization={true}
                isGroup={true}
                isSubGroup={false}
                isTeam={false}
                addButtonLabel='Add Content'
                modelHeader="Delete Content?"
                onAddHandler={onAddContentHandler}
                onDeleteHandler={onDeleteHandler} 
                customDeleteModal={showDeleteModal}
            />
            <div className={managementStyles.GridContainer}>
                <PerinHealthGrid
                    dataList={perinHealthGrid?.dataList || []}
                    headers={headers}
                    showCheckbox={true}
                    showEditIcon={true}
                    onRowClick={onRowClickHandler}
                    onDeleteRow={onDeleteHandler}
                    showHeaderFilter={true}
                    gridHeight='100%'
                />
            </div>
        </div>
    );
};

export default ContentManagement;
